<template>
  <li class="company-wall-products__item">
    <template v-if="hasEndProducts">
      <SButton
        action-type="tertiary-soft"
        :aria-expanded="isOpen"
        aria-haspopup="true"
        class="company-wall-products__button"
        color="gray"
        text-align="between"
        @click="onButtonClick"
      >
        <span class="company-wall-products__button-text">
          {{ parent.name }}
        </span>
        <i
          aria-hidden="true"
          class="fa-regular fa-chevron-down company-wall-products__chevron"
          :class="{ 'company-wall-products__chevron--open': isOpen }"
        />
      </SButton>
      <PPopover ref="popoverEl" @hide="isOpen = false">
        <div class="company-wall-products__popover">
          <ul class="company-wall-products__end-products">
            <li
              v-for="endProduct in parent.endProducts"
              :key="endProduct.id"
              class="company-wall-products__end-product"
            >
              <i
                aria-hidden="true"
                class="fal fa-circle-check company-wall-products__end-product-check"
              />
              {{ endProduct.name }}
            </li>
          </ul>
          <hr class="company-wall-products__popover-divider" />
          <SButton
            action-type="primary"
            class="company-wall-products__popover-link"
            color="success"
            size="sm"
            suffix-icon="far fa-arrow-right"
            :to="buildProductUrl(parent.slug)"
          >
            {{ tr("main_site.company_wall.cta.open_direct_match_form") }}
          </SButton>
        </div>
      </PPopover>
    </template>
    <SButton
      v-else
      action-type="tertiary-soft"
      class="company-wall-products__button"
      color="gray"
      suffix-icon="far fa-arrow-right"
      text-align="between"
      :to="buildProductUrl(parent.slug)"
    >
      <span class="company-wall-products__button-text">
        {{ parent.name }}
      </span>
    </SButton>
  </li>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";

import { SButton } from "@solvari/common-fe";
import { PPopover } from "@solvari/components";
import { useI18nMainSiteCompanyWall } from "@solvari/translations";

import { ziggy } from "@/shared/libs/ziggy";

const props = defineProps<{
  customerId: number;
  parent: App.Data.CompanyWall.CompanyWallProductWithEndProductsData;
}>();

const { tr } = useI18nMainSiteCompanyWall();

const hasEndProducts = computed(() => props.parent.endProducts.length > 0);

const popoverEl = useTemplateRef<InstanceType<typeof PPopover>>("popoverEl");
const isOpen = ref(false);

function onButtonClick(event: Event) {
  isOpen.value = !isOpen.value;
  popoverEl.value?.toggle(event);
}

function buildProductUrl(geoProductSlug: string): string {
  return ziggy(
    "mainSite.geoProduct.leadForm",
    { formSlug: undefined, geoProductSlug },
    { customerId: props.customerId },
  );
}
</script>

<style lang="postcss">
.company-wall-products {
  &__item {
    @apply list-none;
  }

  &__button {
    @apply flex w-full items-center gap-2;
  }

  &__button-text {
    @apply flex-1 text-left;
  }

  &__chevron {
    @apply shrink-0 transition-transform duration-200;

    &--open {
      @apply rotate-180;
    }
  }

  &__popover {
    @apply flex flex-col gap-2;
  }

  &__popover-divider {
    @apply my-1 border-t border-gray-200;
  }

  &__popover-link {
    @apply w-full text-base font-semibold;
  }

  &__end-products {
    @apply flex flex-col pl-0 gap-2;
  }

  &__end-product {
    @apply flex list-none items-center text-sm text-gray-600 gap-1;
  }

  &__end-product-check {
    @apply shrink-0 text-green-500;
  }
}
</style>
